import React, { useEffect, useState } from 'react'
import {
  ModalContent,
  ModalTitle,
  ModalFooter,
  Button,
  Paragraph,
} from 'suomifi-ui-components'
import {
  clearCountdownToExpireDialog,
  getSessionExpiresInMs,
  refreshSessionNow,
  refreshSessionTimerCountdown,
} from 'todistukset-ui/src/vtj/stores/actions/session-timer-actions'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import type { DialogItem } from 'todistukset-ui/src/vtj/stores/dialogs.mobx.store'
import type { TodistuksetUi } from 'todistukset-ui/src/vtj/common-types'

export const parseUnitValuesFromSessionExpiresInMs = ({
  sessionExpiresInMs,
}: {
  sessionExpiresInMs: number
}): { minutes: number; seconds: number } => {
  const duration = new Date(sessionExpiresInMs)
  const minutes = duration.getUTCMinutes()
  const seconds = duration.getUTCSeconds()

  return {
    minutes,
    seconds,
  }
}

const tickSessionExpireCountdown = ({
  intervalToSessionExpire,
  sessionExpiresAt,
}: {
  intervalToSessionExpire?: number
  sessionExpiresAt?: string
}): {
  hasStarted: boolean
  minutes: number
  seconds: number
} => {
  const hasStarted = true
  let minutes = 0
  let seconds = 0
  if (!sessionExpiresAt) {
    window.clearInterval(intervalToSessionExpire)
    return { hasStarted, minutes, seconds }
  }

  const { sessionExpiresInMs } = getSessionExpiresInMs({
    sessionExpiresAt,
  })
  if (sessionExpiresInMs > 0) {
    ;({ minutes, seconds } = parseUnitValuesFromSessionExpiresInMs({
      sessionExpiresInMs,
    }))
  } else {
    window.clearInterval(intervalToSessionExpire)
  }

  return { hasStarted, minutes, seconds }
}

const useSessionExpiresCountdown = ({
  sessionExpiresAt,
  setTimeRemaining,
}: {
  sessionExpiresAt?: string
  setTimeRemaining: React.Dispatch<
    React.SetStateAction<{
      hasStarted: boolean
      minutes: number
      seconds: number
    }>
  >
}): void =>
  useEffect(() => {
    clearCountdownToExpireDialog()
    setTimeRemaining(tickSessionExpireCountdown({ sessionExpiresAt }))

    const intervalToSessionExpire = window.setInterval(() => {
      setTimeRemaining(
        tickSessionExpireCountdown({
          intervalToSessionExpire,
          sessionExpiresAt,
        })
      )
    }, 1000)

    return (): void => {
      window.clearInterval(intervalToSessionExpire)
    }
  }, [sessionExpiresAt, setTimeRemaining])

const useCheckStatus = ({
  isAboutToExpire,
  isCheckingStatus,
  sessionExpiresAt,
  setIsCheckingStatus,
}: {
  isAboutToExpire: boolean
  isCheckingStatus: boolean
  sessionExpiresAt?: string
  setIsCheckingStatus: React.Dispatch<React.SetStateAction<boolean>>
}): void => {
  useEffect(() => {
    if (!isCheckingStatus && isAboutToExpire) {
      setIsCheckingStatus(true)
      setTimeout(async () => {
        await refreshSessionTimerCountdown({
          isExpireDialogAlreadyVisible: true,
        })
        setIsCheckingStatus(false)
      }, 0)
    } else if (isCheckingStatus && !isAboutToExpire) {
      setIsCheckingStatus(false)
    }
  }, [isAboutToExpire, sessionExpiresAt]) // eslint-disable-line react-hooks/exhaustive-deps
}

export const SessionAboutToExpireDialogContent = ({
  dialogItem,
  sessionTimer: { sessionExpiresAt },
}: {
  dialogItem: DialogItem
  sessionTimer:
    | TodistuksetUi.Store.InitSessionTimer
    | TodistuksetUi.Store.SessionTimer
}): React.JSX.Element => {
  const [timeRemaining, setTimeRemaining] = useState({
    hasStarted: false,
    minutes: 0,
    seconds: 0,
  })
  const [isCheckingStatus, setIsCheckingStatus] = useState(false)

  useSessionExpiresCountdown({
    sessionExpiresAt,
    setTimeRemaining,
  })

  const { hasStarted, minutes, seconds } = timeRemaining
  const hasTimeRemaining = Math.max(minutes, seconds) > 0
  const isAboutToExpire = hasStarted && !hasTimeRemaining

  useCheckStatus({
    isAboutToExpire,
    isCheckingStatus,
    sessionExpiresAt,
    setIsCheckingStatus,
  })

  const labelledby = 'session-expiring-title'
  const describedby = 'session-expiring-description'
  return (
    <React.Fragment>
      <ModalContent
        role="alertdialog"
        aria-labelledby={labelledby}
        aria-describedby={describedby}
      >
        <ModalTitle id={labelledby}>
          <LocalizedString stringKey="sessionIsExpiring" />
        </ModalTitle>
        <Paragraph
          id={describedby}
          data-test-id={`${dialogItem.id}-dialog-body`}
        >
          <LocalizedString
            stringKey="yourSessionIsAboutToExpireIn"
            values={{ timeRemaining }}
          />
        </Paragraph>
      </ModalContent>
      <ModalFooter>
        <Button
          onClick={() => {
            dialogItem.active?.resolveDialog('refreshSessionNow')
            refreshSessionNow()
          }}
          data-test-id={`${dialogItem.id}-dialog-button`}
          disabled={isCheckingStatus}
        >
          <LocalizedString stringKey="continueSession" />
        </Button>
      </ModalFooter>
    </React.Fragment>
  )
}
